import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import type { Course } from "@/types/course";
import { fetchCourses } from "@/api/courses";
import CourseList from "./components/CourseList";
import ComparisonTable from "./components/ComparisonTable";
import FilterSortBar from "./components/FilterSortBar";
import { sortCourses, type SortDir, type SortKey } from "@/utils/courseSort";
import { trackEvent } from "@/utils/analytics";
import { usePageMeta } from "@/hooks/usePageMeta";

const MIN_SELECT = 2;
const MAX_SELECT = 4;

type LoadState = "loading" | "error" | "ready";

function LoadingState() {
  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-3">
        <div className="h-10 w-32 animate-pulse rounded-md bg-gray-100 dark:bg-gray-800" />
        <div className="h-10 w-32 animate-pulse rounded-md bg-gray-100 dark:bg-gray-800" />
        <div className="h-10 w-40 animate-pulse rounded-md bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="h-44 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        ))}
      </div>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center dark:border-gray-700 dark:bg-gray-900">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
        <i className="ri-error-warning-line text-2xl" />
      </div>
      <p className="mt-4 text-sm text-gray-600 break-keep dark:text-gray-300">
        과정 데이터를 불러오지 못했습니다.
      </p>
      <p className="mt-1 text-xs text-gray-400 break-keep dark:text-gray-500">
        네트워크 상태를 확인한 뒤 다시 시도해 주세요.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 whitespace-nowrap rounded-md bg-brand-700 px-4 py-2 text-sm text-white transition-colors hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-gray-900"
      >
        다시 시도
      </button>
    </div>
  );
}

export default function CourseComparisonPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("default");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [region, setRegion] = useState("전체");
  const [typeCode, setTypeCode] = useState("전체");

  usePageMeta({
    title: "훈련과정 비교 — 다봄",
    description:
      "국비지원 훈련과정을 2~4개 골라 수강비·만족도·취업률을 나란히 비교하세요. 다봄 훈련과정 비교.",
    path: "/과정비교",
  });

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const data = await fetchCourses();
      setCourses(data);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tableShownFired = useRef(false);

  useEffect(() => {
    trackEvent("compare_view");
  }, []);

  const regionOptions = useMemo(
    () => Array.from(new Set(courses.map((c) => c.region))),
    [courses],
  );
  const typeOptions = useMemo(
    () => Array.from(new Set(courses.map((c) => c.typeCode))),
    [courses],
  );

  const visibleCourses = useMemo(() => {
    let list = courses;
    if (region !== "전체") list = list.filter((c) => c.region === region);
    if (typeCode !== "전체") list = list.filter((c) => c.typeCode === typeCode);
    return sortCourses(list, sortKey, sortDir);
  }, [courses, region, typeCode, sortKey, sortDir]);

  const selectedCourses = useMemo(
    () => courses.filter((c) => selectedIds.includes(c.id)),
    [courses, selectedIds],
  );

  const toggleCourse = (id: string) => {
    const course = courses.find((c) => c.id === id);
    const courseName = course?.name ?? "";
    const isSelected = selectedIds.includes(id);

    if (isSelected) {
      const next = selectedIds.filter((x) => x !== id);
      setSelectedIds(next);
      trackEvent("compare_deselect", {
        course_name: courseName,
        selected_count: next.length,
      });
      return;
    }

    if (selectedIds.length >= MAX_SELECT) return;

    const next = [...selectedIds, id];
    setSelectedIds(next);
    trackEvent("compare_select", {
      course_name: courseName,
      selected_count: next.length,
    });
    if (next.length >= MAX_SELECT) {
      trackEvent("compare_limit_hit");
    }
  };

  const removeCourse = (id: string) => {
    const course = courses.find((c) => c.id === id);
    const next = selectedIds.filter((x) => x !== id);
    setSelectedIds(next);
    trackEvent("compare_deselect", {
      course_name: course?.name ?? "",
      selected_count: next.length,
    });
  };

  const clearAll = () => setSelectedIds([]);

  useEffect(() => {
    if (selectedIds.length >= MIN_SELECT && !tableShownFired.current) {
      tableShownFired.current = true;
      trackEvent("compare_table_shown", { selected_count: selectedIds.length });
    }
    if (selectedIds.length < MIN_SELECT) {
      tableShownFired.current = false;
    }
  }, [selectedIds.length]);

  const atMax = selectedIds.length >= MAX_SELECT;
  const canCompare = selectedCourses.length >= MIN_SELECT;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12">
        <header className="mb-8">
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-gray-100 md:text-3xl">
            훈련과정 비교
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-600 break-keep dark:text-gray-400 md:text-base">
            관심 있는 훈련과정을 2~4개 골라 나란히 비교해 보세요.
          </p>
        </header>

        {loadState === "loading" && <LoadingState />}

        {loadState === "error" && <ErrorState onRetry={load} />}

        {loadState === "ready" && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  선택{" "}
                  <strong className="text-brand-700 dark:text-brand-400">{selectedIds.length}</strong>{" "}
                  / {MAX_SELECT}개
                </span>
                {!canCompare && (
                  <span className="text-xs text-gray-500 break-keep dark:text-gray-400">
                    · 비교하려면 2개 이상 선택하세요
                  </span>
                )}
                {atMax && (
                  <span className="text-xs text-amber-600 break-keep dark:text-amber-400">
                    · 최대 4개까지 비교할 수 있어요
                  </span>
                )}
              </div>
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center whitespace-nowrap text-sm text-gray-500 underline transition-colors hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-400 dark:hover:text-gray-300 dark:focus-visible:ring-brand-400"
                >
                  전체 해제
                </button>
              )}
            </div>

            <div className="mt-3">
              <FilterSortBar
                regions={regionOptions}
                typeCodes={typeOptions}
                region={region}
                typeCode={typeCode}
                sortKey={sortKey}
                sortDir={sortDir}
                onRegionChange={setRegion}
                onTypeChange={setTypeCode}
                onSortKeyChange={setSortKey}
                onSortDirChange={setSortDir}
              />
            </div>

            <section className="mt-4" aria-label="비교할 과정 선택">
              {visibleCourses.length > 0 ? (
                <CourseList
                  courses={visibleCourses}
                  selectedIds={selectedIds}
                  maxSelect={MAX_SELECT}
                  sortKey={sortKey}
                  onToggle={toggleCourse}
                />
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center dark:border-gray-700 dark:bg-gray-900">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
                    <i className="ri-file-search-line text-2xl" />
                  </div>
                  <p className="mt-4 text-sm text-gray-600 break-keep dark:text-gray-300">
                    조건에 맞는 과정이 없습니다.
                  </p>
                  <p className="mt-1 text-xs text-gray-400 break-keep dark:text-gray-500">
                    지역·유형 필터를 바꿔 다시 시도해 보세요.
                  </p>
                </div>
              )}
            </section>

            <section className="mt-8" aria-label="비교 표">
              {canCompare ? (
                <>
                  <p className="mb-3 text-xs text-gray-500 break-keep dark:text-gray-400">
                    '-'는 데이터 없음을 뜻합니다(0%와 다릅니다).
                  </p>
                  <ComparisonTable
                    courses={selectedCourses}
                    onRemove={removeCourse}
                  />
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center dark:border-gray-700 dark:bg-gray-900">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
                    <i className="ri-bar-chart-2-line text-2xl" />
                  </div>
                  <p className="mt-4 text-sm text-gray-600 break-keep dark:text-gray-300">
                    비교할 과정을 위 목록에서 2개 이상 선택해 주세요.
                  </p>
                  <p className="mt-1 text-xs text-gray-400 break-keep dark:text-gray-500">
                    최소 2개, 최대 4개까지 나란히 비교할 수 있어요.
                  </p>
                </div>
              )}
            </section>
          </>
        )}

      </div>
  );
}